import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, type Ref } from "vue";
import type { ModelExecution } from "@/shared/agent-display/model-execution";

/*
 * Two functions used to live here and were called by nothing.
 *
 * `useRecentTurns` read `/v3/agents/turns` — the same endpoint `useTurns` in the
 * usage feature reads, which is what the Consumo tab actually renders. Two composables
 * over one endpoint means one of them drifts, and the unused one drifts silently.
 *
 * `useRunTurn` posted to `/v3/agents/run`. Every chat surface streams through
 * `stream.ts` instead, because a five-minute builder turn behind a single POST
 * shows a spinner and nothing else. Deleted rather than left as a second way to do
 * the same thing.
 */
import type { ToolNote } from "@/shared/agent-story/story";
import { request } from "@/shared/http/client";
import type { Say } from "./tool-labels";

export type AgentMode = "builder" | "operator" | "interaction";
export type ModelTier = "fast" | "smart" | "premium";

/**
 * The persona, in the five parts the form edits (migration 0055).
 *
 * One box asking for "a persona" gets a paragraph nobody can review. Five named
 * questions are answerable one at a time, and the agent still receives the sum —
 * `personaPreview` is that sum, and it is read-only.
 */
export interface PersonaParts {
	objective: string | null;
	tone: string | null;
	guardrails: string | null;
	knowledgeScope: string | null;
	escalation: string | null;
}

export type PersonaField = keyof PersonaParts;

/**
 * Os três sexos que um atendente pode ter. Espelha o CHECK de `agent.sex`.
 *
 * `neutral` existe aqui e não existe no pacote de retratos (só `women/` e
 * `men/`): um agente neutro tem voz e não tem rosto automático, que é a mesma
 * regra do vazio que a 0110 já seguia.
 */
export const AGENT_SEXES = ["male", "female", "neutral"] as const;
export type AgentSex = (typeof AGENT_SEXES)[number];

export interface Agent extends PersonaParts {
	code: string;
	name: string;
	mode: AgentMode;
	tier: ModelTier;
	/** The free text as STORED — never the sum, which would compose itself twice. */
	persona: string | null;
	/** What the agent actually receives: the five parts plus the free text. */
	personaPreview: string | null;
	enabledTools: string[];
	/**
	 * O PAPEL deste agente, na mesma escala das pessoas.
	 *
	 * Substituiu o pseudo-papel "workspace", pelo qual um agente atravessava o
	 * `access` de toda entidade. O ALCANCE dele não vem por aqui: mora nas tabelas
	 * de acesso, e é a tela de Acessos que o desenha — para pessoas e agentes com a
	 * mesma forma e uma porta de escrita só.
	 */
	role: "member" | "admin" | "owner";
	/** O rosto (0110) — retrato do pacote embarcado, ou null para quem nasceu sem. */
	avatarUrl: string | null;
	/**
	 * O SEXO DO ATENDENTE (migração 0140) — um campo, duas perguntas.
	 *
	 * Escolhe a VOZ com que ele fala (a padrão daquele sexo, na tela DEV de vozes)
	 * e o ROSTO que o sorteio lhe dá. Eram duas decisões soltas até 26/08: o
	 * retrato vinha de um gênero que o blueprint usava e descartava, e a voz não
	 * sabia de nada — uma atendente com cara de mulher falava com a voz neutra.
	 *
	 * `null` é "ninguém disse", que é como nasce todo agente feito pela tela: ele
	 * fala com a voz neutra do provedor padrão, e não fica mudo.
	 */
	sex: AgentSex | null;
	isActive: boolean;
	/**
	 * Whether `send_message` from this agent goes out or waits for a person (0046).
	 *
	 * Only the TOOL. An interaction agent replying inside a conversation does not go
	 * through it, so turning this on never stops the assistant answering customers.
	 */
	sendPolicy: "send" | "ask";
	/** Every tool this agent's MODE can see — the ceiling for enabledTools. */
	availableTools: string[];
}

/**
 * O mínimo que uma CONVERSA precisa saber sobre com quem fala.
 *
 * `StreamingChat` e o topo da doca pediam o `Agent` inteiro — persona, ferramentas,
 * política de envio — para desenhar um nome, um rosto e escolher o chão das
 * sugestões. A doca do Ben lê o agente pela porta nova (`use-ben.ts`), que devolve
 * exactamente estes quatro campos e mais nada; pedir o registo completo obrigaria a
 * uma segunda leitura da lista de agentes só para preencher o que a tela não usa.
 */
export type ChatAgent = Pick<Agent, "code" | "name" | "mode" | "avatarUrl">;

export interface AgentsResponse {
	modelAvailable: boolean;
	modelProblem: string | null;
	agents: Agent[];
}

export interface ToolInfo {
	name: string;
	description: string;
	modes: AgentMode[];
}

export interface TurnStep {
	tool: string;
	input: string;
	/**
	 * Absent while the call is still IN FLIGHT: a mission's progress writes carry
	 * the call the model announced before its tool came back, and "no output yet"
	 * is what the screen paints as "rodando…" (see `isPending`).
	 */
	output?: string;
	ms: number;
	/**
	 * Whether the tool said it failed. Absent on steps recorded before the server
	 * carried the flag — the screen falls back to sniffing the output text.
	 */
	isError?: boolean;
	/**
	 * O ACHADO do passo, em dados — o que a história da missão desenha em frases
	 * (`shared/agent-story/story.ts`). Opcional e quase sempre ausente: uma
	 * ferramenta que não soube dizer o que achou desenha como sempre desenhou.
	 */
	note?: ToolNote;
	/** As tarefas que o passo reportou enquanto corria — ver `LiveTool.tasks`. */
	tasks?: readonly ToolTask[];
}

/** Uma tarefa de um passo — a forma do `ToolTask` do servidor. */
export interface ToolTask {
	readonly id: string;
	readonly label: string;
	readonly worker?: number;
	readonly status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
}

export interface RunResult {
	reply: string;
	/** `---` in the reply splits it into separate bubbles, as a channel would. */
	bubbles: string[];
	/** O raciocínio do provedor, uma entrada por ronda. */
	thinking: string[];
	/** O texto que o modelo escreveu e não foi entregue. Ver `AgentWorking`. */
	withheld: string[];
	steps: TurnStep[];
	silent: boolean;
	usage: { inputTokens: number; outputTokens: number };
}

export function useAgents() {
	return useQuery({
		queryKey: ["agents"],
		queryFn: () => request<AgentsResponse>("/v3/agents"),
	});
}

export function useAgentTools() {
	return useQuery({
		queryKey: ["agent-tools"],
		// The catalogue does not change between requests within a session.
		staleTime: 10 * 60 * 1000,
		queryFn: () => request<{ tools: ToolInfo[] }>("/v3/agents/tools"),
	});
}

/**
 * What ONE agent sees and may call — `GET /v3/agents/:code/capabilities`.
 *
 * Computed on the server with the same functions the turn uses (`toolMenu`, the
 * callable-artifacts reader), so this screen cannot promise a tool or an
 * artifact the turn would never offer. `sees` is counts and booleans, never the
 * content: memory and library belong to another context.
 */
export interface AgentCapabilities {
	readonly agent: { code: string; name: string; mode: AgentMode; tier: ModelTier };
	/** The door the menu was computed for — without a conversation, always the panel's. */
	readonly door: string;
	readonly tools: ReadonlyArray<{ name: string; description: string }>;
	/** As funções que ele SABE FAZER: as ativas ao alcance dele, com o porquê. */
	readonly artifacts: readonly ArtifactGrant[];
	/** As outras funções no ar — o que ele poderia fazer se alguém o nomeasse. */
	readonly couldRun: readonly ArtifactGrant[];
	readonly sees: {
		businessProfile: boolean;
		brandRules: boolean;
		memoryFacts: number;
		knowledgeDocuments: number;
		persona: boolean;
	};
}

/**
 * POR ONDE este agente chega a uma função — o que decide o que a tela oferece.
 *
 * `mode` vem de `access.modes` e não se desliga por agente: tirá-lo daqui seria
 * tirá-lo de todos os agentes do mesmo modo, então a tela diz a razão em vez de
 * desenhar um interruptor que não cumpre. `agent` é o código dele em
 * `access.agents` — o único caso com interruptor dos dois lados. `none` é uma
 * função no ar fora do alcance dele, e é o que o interruptor liga.
 *
 * HAVIA UM QUARTO, `open` — "uma leitura sem acesso declarado, aberta a qualquer
 * agente desde sempre". Nunca foi verdade do lado do servidor depois de as
 * espécies saírem: sem `access`, o portão do artefato recusa TODO agente, e uma
 * função sem acesso declarado só corre por uma regra ou pela mão de uma pessoa.
 * O servidor nunca enviou este valor; o que havia era a promessa errada na tela.
 * Saiu em 10/09/2026, com a frase que a desenhava.
 */
export type ArtifactAccessVia = "mode" | "agent" | "none";

export interface ArtifactGrant {
	readonly code: string;
	readonly name: string;
	readonly intent: string;
	/** A frase curta para gente — a mesma que o painel da função mostra. */
	readonly description?: string;
	readonly inputs: ReadonlyArray<{
		name: string;
		type: string;
		required?: boolean;
		label?: string;
		description?: string;
	}>;
	readonly via: ArtifactAccessVia;
	/**
	 * Os agentes NOMEADOS nesta função hoje (`access.agents`), e não só este.
	 *
	 * É o que o interruptor precisa para gravar: ele manda a lista inteira, e uma
	 * tela que só conhecesse o próprio código apagaria os outros nomes ao
	 * ligar-se a si mesma.
	 */
	readonly agents: readonly string[];
	/** Os modos que a função abre (`access.modes`). */
	readonly modes?: readonly string[];
	/** As regras ativas que a rodam — o porquê de ela funcionar sem este agente. */
	readonly rules?: readonly string[];
}

export function useAgentCapabilities(code: Ref<string>) {
	return useQuery({
		queryKey: computed(() => ["agents", code.value, "capabilities"] as const),
		enabled: computed(() => !!code.value),
		queryFn: () =>
			request<AgentCapabilities>(`/v3/agents/${encodeURIComponent(code.value)}/capabilities`),
	});
}

/**
 * NOMEAR (ou desnomear) um agente numa função — o interruptor de "sabe fazer".
 *
 * `PATCH /v3/artifacts/:code/access` manda a lista INTEIRA de agentes nomeados,
 * e não um "adiciona"/"remove": a tela sabe a lista que está a mostrar, e um
 * pedido que dependesse do que já lá estava daria dois resultados diferentes
 * para o mesmo clique repetido. O servidor grava na versão no ar, sem versão
 * nova — quem pode chamar é uma outorga, não comportamento.
 *
 * Mora no dicionário dos AGENTES e não no dos artefatos porque o gesto é da
 * tela do agente: é ali que a pergunta "ele sabe fazer isto?" é feita.
 */
export function useSetArtifactAccess() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: { artifact: string; agents: string[] }) =>
			request<{ agents: string[] }>(`/v3/artifacts/${encodeURIComponent(input.artifact)}/access`, {
				method: "PATCH",
				body: { agents: input.agents },
			}),
		// As capacidades de TODOS os agentes: a mesma função pode estar nomeada
		// para mais do que um, e a lista do outro ficaria a mentir.
		onSuccess: (_removed, code) => {
			// O que era DESTE agente sai do cache em vez de recarregar: ele já não existe,
			// e recarregar é um 404 por consulta (capabilities, etc.).
			if (code) queryClient.removeQueries({ queryKey: ["agents", code] });
			return queryClient.invalidateQueries({
				predicate: ({ queryKey }) =>
					(queryKey[0] === "agents" && (!code || queryKey[1] !== code)) ||
					queryKey[0] === "blueprints",
			});
		},
	});
}

/**
 * Creating an agent.
 *
 * The gap this closes is not small: `AgentRepository.create` had exactly one caller —
 * onboarding, seeding three agents at sign-up — so a workspace had the agents it was born
 * with and no way to make another. A second attendant with its own tone for a different
 * channel could not be expressed at all.
 */
export function useCreateAgent() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (
			input: Partial<PersonaParts> & {
				code: string;
				name: string;
				mode: "builder" | "operator" | "interaction";
				tier?: ModelTier;
				persona?: string | null;
				/** Ver `Agent.sex`. Nasce com ele para que a primeira fala já saia certa. */
				sex?: AgentSex | null;
			},
		) => request<Agent>("/v3/agents", { method: "POST", body: input }),
		onSuccess: (_removed, code) => {
			// O que era DESTE agente sai do cache em vez de recarregar: ele já não existe,
			// e recarregar é um 404 por consulta (capabilities, etc.).
			if (code) queryClient.removeQueries({ queryKey: ["agents", code] });
			return queryClient.invalidateQueries({
				predicate: ({ queryKey }) =>
					(queryKey[0] === "agents" && (!code || queryKey[1] !== code)) ||
					queryKey[0] === "blueprints",
			});
		},
	});
}

export function useUpdateAgent() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (
			input: Partial<PersonaParts> & {
				code: string;
				name?: string;
				tier?: ModelTier;
				persona?: string | null;
				enabledTools?: string[];
				role?: Agent["role"];
				isActive?: boolean;
				sendPolicy?: "send" | "ask";
				sex?: AgentSex | null;
			},
		) => {
			const { code, ...body } = input;
			return request<Agent>(`/v3/agents/${encodeURIComponent(code)}`, { method: "PATCH", body });
		},
		onSuccess: (_removed, code) => {
			// O que era DESTE agente sai do cache em vez de recarregar: ele já não existe,
			// e recarregar é um 404 por consulta (capabilities, etc.).
			if (code) queryClient.removeQueries({ queryKey: ["agents", code] });
			return queryClient.invalidateQueries({
				predicate: ({ queryKey }) =>
					(queryKey[0] === "agents" && (!code || queryKey[1] !== code)) ||
					queryKey[0] === "blueprints",
			});
		},
	});
}

/**
 * A copy of an agent, born switched off.
 *
 * How a second assistant is actually made: take the one that works and change two
 * sentences. The server copies the mode, the tier, the persona and the tools —
 * and none of the turns, because the history belongs to what happened.
 */
export function useDuplicateAgent() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: { source: string; code: string; name: string }) =>
			request<Agent>(`/v3/agents/${encodeURIComponent(input.source)}/duplicate`, {
				method: "POST",
				body: { code: input.code, name: input.name },
			}),
		onSuccess: (_removed, code) => {
			// O que era DESTE agente sai do cache em vez de recarregar: ele já não existe,
			// e recarregar é um 404 por consulta (capabilities, etc.).
			if (code) queryClient.removeQueries({ queryKey: ["agents", code] });
			return queryClient.invalidateQueries({
				predicate: ({ queryKey }) =>
					(queryKey[0] === "agents" && (!code || queryKey[1] !== code)) ||
					queryKey[0] === "blueprints",
			});
		},
	});
}

export function useDeleteAgent() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (code: string) =>
			request<{ removed: string }>(`/v3/agents/${encodeURIComponent(code)}`, { method: "DELETE" }),
		onSuccess: (_removed, code) => {
			// O que era DESTE agente sai do cache em vez de recarregar: ele já não existe,
			// e recarregar é um 404 por consulta (capabilities, etc.).
			if (code) queryClient.removeQueries({ queryKey: ["agents", code] });
			return queryClient.invalidateQueries({
				predicate: ({ queryKey }) =>
					(queryKey[0] === "agents" && (!code || queryKey[1] !== code)) ||
					queryKey[0] === "blueprints",
			});
		},
	});
}

/**
 * Why the server refused to delete an agent, in words.
 *
 * Mapped from the CODE and not shown as the server's sentence, same as
 * `describeTeamError`: none of these is an accident somebody triggers once, and
 * each one names the thing to do first.
 *
 * `say` is the caller's `t`, handed in — the same arrangement
 * `features/auth/api/errors.ts` uses, and for the same reason: this is called
 * from a `catch`, not from a setup, so it has no scope of its own.
 */
export function describeAgentError(e: unknown, say: Say): string {
	const payload = (e as { payload?: { error?: string; message?: string } }).payload;
	if (payload?.message) return payload.message;
	const kind = (e as { kind?: string }).kind;
	if (kind === "forbidden") return say("error.forbidden");
	if (kind === "network" || kind === "timeout") return say("error.unreachable");
	return say("error.generic");
}

// ---------------------------------------------------------------------------
// Conversations. Separate from `turns`, which is the audit log.
// ---------------------------------------------------------------------------

/**
 * What a session is ABOUT (migration 0056).
 *
 * Absent is the ordinary panel thread, the one picked from a sidebar. Present is
 * a thread ANCHORED to something: one per person per customer conversation, so
 * reopening a conversation reopens the same thread instead of starting one per
 * render.
 */
export interface SessionSubject {
	readonly kind: "conversation";
	readonly id: string;
}

export interface AgentSession {
	readonly id: string;
	readonly agentCode: string;
	readonly title: string | null;
	readonly messageCount: number;
	readonly subject: SessionSubject | null;
	readonly createdAt: string;
	readonly updatedAt: string;
}

export interface SessionMessage {
	readonly id: string;
	/** `agent`: o resultado de um subagente em segundo plano — nem a pessoa, nem o Ben. */
	readonly role: "user" | "assistant" | "agent";
	readonly content: string;
	readonly steps: TurnStep[];
	readonly usage: { inputTokens: number; outputTokens: number };
	/** O que estava anexado, por ENDEREÇO e nunca pelos bytes (migração 0071): a
	 *  prévia era uma URL `blob:`, que morre com a aba. Opcional porque um servidor
	 *  anterior à migração não manda o campo, e vazio é a resposta normal. */
	readonly images?: string[];
	/**
	 * Os arquivos que a fala entregou, para reabrir a conversa devolver o anexo e
	 * não a lembrança dele.
	 *
	 * Cru: quem dá forma a um anexo é `toAttachment`, um leitor só para as três
	 * estradas por onde ele chega (o evento do fio, o quadro `done`, e esta
	 * releitura). Opcional porque um servidor que ainda não o mande funciona.
	 */
	readonly attachments?: readonly unknown[];
	/** A conta do turno, agora guardada na fala (0148). Ausente nas falas da
	 *  pessoa e em tudo o que foi escrito antes da migração. */
	readonly meter?: {
		execution?: ModelExecution;
		provider?: string;
		model?: string;
		costMicroUsd?: number;
		durationMs?: number;
		credits?: number | null;
	} | null;
	/** O turno de auditoria — o que devolve o 👍 a uma conversa reaberta. */
	readonly turnId?: string | null;
	readonly createdAt: string;
}

export function useSessions() {
	return useQuery({
		queryKey: ["agent-sessions"],
		queryFn: async (): Promise<AgentSession[]> => {
			const res = await request<{ sessions: AgentSession[] }>("/v3/agents/sessions");
			return res.sessions;
		},
	});
}

export function useCreateSession() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { agent: string; subject?: SessionSubject | null }) =>
			request<AgentSession>("/v3/agents/sessions", {
				method: "POST",
				body: { agent: input.agent, ...(input.subject ? { subject: input.subject } : {}) },
			}),
		onSuccess: (session) => {
			void client.invalidateQueries({ queryKey: ["agent-sessions"] });
			// A porta do Ben devolveu `session: null` antes desta thread existir, e
			// é dessa resposta que a coluna se lembra. Sem a invalidação, reabrir a
			// conversa abriria uma thread nova por cima da que acabou de nascer.
			if (session.subject) void client.invalidateQueries({ queryKey: ["ben"] });
		},
	});
}

export function useDeleteSession() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) => request(`/v3/agents/sessions/${id}`, { method: "DELETE" }),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["agent-sessions"] });
			// A thread arquivada pode ser a que o servidor devolveu como a DESTA
			// porta: sem esta linha a coluna continuaria a apontar para uma conversa
			// que já não existe, e a próxima pergunta escreveria no vazio.
			void client.invalidateQueries({ queryKey: ["ben"] });
		},
	});
}

export async function loadSessionMessages(id: string): Promise<SessionMessage[]> {
	const res = await request<{ messages: SessionMessage[] }>(`/v3/agents/sessions/${id}/messages`);
	return res.messages;
}

/**
 * Apaga UMA fala da thread — o lixinho da barra, como na V2.
 *
 * Quem tira a fala da tela é quem chamou (a lista é estado local do chat);
 * nenhuma invalidação de query, porque as mensagens não vivem numa query.
 */
export function useDeleteMessage() {
	return useMutation({
		mutationFn: async (input: { sessionId: string; messageId: string }) =>
			request(`/v3/agents/sessions/${input.sessionId}/messages/${input.messageId}`, {
				method: "DELETE",
			}),
	});
}

export async function searchSessions(
	query: string,
	agent: string,
	signal?: AbortSignal,
): Promise<AgentSession[]> {
	const params = new URLSearchParams({ q: query, agent });
	const result = await request<{ sessions: AgentSession[] }>(`/v3/agents/sessions?${params}`, {
		signal,
	});
	return result.sessions;
}
